const API_BASE_URL = import.meta.env.VITE_API_URL || 
  (window.location.hostname === 'localhost' ? '' : 'https://reactor-jarvis-app2.valleyintest.workers.dev');

interface ApiOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function api<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {} } = options;
  
  const config: RequestInit = {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials: 'include',
  };
  
  if (body) {
    config.body = JSON.stringify(body);
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, config);
  
  const data = await response.json();
  
  if (!response.ok) {
    throw new ApiError(
      response.status,
      data.error || 'UnknownError',
      data.message || 'エラーが発生しました'
    );
  }
  
  return data as T;
}

// 認証API
export const authApi = {
  login: (email: string, password: string) =>
    api<{ success: boolean; token: string; user: User }>('/api/auth/login', {
      method: 'POST',
      body: { email, password },
    }),
  
  logout: () =>
    api<{ success: boolean }>('/api/auth/logout', { method: 'POST' }),
  
  me: () =>
    api<User>('/api/auth/me'),
  
  changePassword: (currentPassword: string, newPassword: string) =>
    api<{ success: boolean }>('/api/auth/change-password', {
      method: 'POST',
      body: { currentPassword, newPassword },
    }),
  
  resetPasswordRequest: (email: string) =>
    api<{ success: boolean }>('/api/auth/reset-password-request', {
      method: 'POST',
      body: { email },
    }),
  
  validateInvite: (token: string) =>
    api<{ valid: boolean; email: string; role: string }>(`/api/auth/invite/${token}`),
  
  validateInvitation: (token: string) =>
    api<{ valid: boolean; email: string; role: string }>(`/api/auth/invite/${token}`),
  
  acceptInvite: (token: string, name: string, password: string) =>
    api<{ success: boolean; token: string; user: User }>(`/api/auth/invite/${token}/accept`, {
      method: 'POST',
      body: { name, password },
    }),
  
  acceptInvitation: (token: string, name: string, password: string) =>
    api<{ success: boolean; token: string; user: User }>(`/api/auth/invite/${token}/accept`, {
      method: 'POST',
      body: { name, password },
    }),
};

// ユーザーAPI
export const usersApi = {
  list: () => api<User[]>('/api/users'),
  
  get: (id: string) => api<User>(`/api/users/${id}`),
  
  invite: (email: string, role: string) =>
    api<{ success: boolean }>('/api/users/invite', {
      method: 'POST',
      body: { email, role },
    }),
  
  update: (id: string, data: Partial<User>) =>
    api<User>(`/api/users/${id}`, {
      method: 'PUT',
      body: data,
    }),
  
  delete: (id: string) =>
    api<{ success: boolean }>(`/api/users/${id}`, { method: 'DELETE' }),
  
  updateProfile: (data: { name?: string; avatarUrl?: string }) =>
    api<User>('/api/users/me/profile', {
      method: 'PUT',
      body: data,
    }),
};

// セッションAPI
export const sessionsApi = {
  getAiModels: () =>
    api<{ currentModel: string; models: AiModel[]; realtimeAvailable: boolean }>('/api/sessions/ai-models'),
  
  createRealtimeSession: () =>
    api<RealtimeSession>('/api/sessions/realtime/session', { method: 'POST' }),
  
  start: () =>
    api<Session>('/api/sessions/start', { method: 'POST' }),
  
  submitKyc: (id: string, name: string, email: string, company: string) =>
    api<{ success: boolean }>(`/api/sessions/${id}/kyc`, {
      method: 'POST',
      body: { name, email, company },
    }),
  
  submitIdea: (id: string, idea: string) =>
    api<Session>(`/api/sessions/${id}/initial-idea`, {
      method: 'POST',
      body: { idea },
    }),
  
  submitAnswer: (id: string, questionId: string, answer: string) =>
    api<Session>(`/api/sessions/${id}/answer`, {
      method: 'POST',
      body: { questionId, answer },
    }),
  
  kyc: (id: string, data: KycData) =>
    api<{ success: boolean; message: string }>(`/api/sessions/${id}/kyc`, {
      method: 'POST',
      body: data,
    }),
  
  initialIdea: (id: string, idea: string) =>
    api<{ message: string; currentQuestion: Question | null; totalQuestions: number }>(`/api/sessions/${id}/initial-idea`, {
      method: 'POST',
      body: { idea },
    }),
  
  answer: (id: string, questionId: string, answer: string, audioUrl?: string) =>
    api<AnswerResponse>(`/api/sessions/${id}/answer`, {
      method: 'POST',
      body: { questionId, answer, audioUrl },
    }),
  
  pause: (id: string) =>
    api<{ success: boolean; message: string }>(`/api/sessions/${id}/pause`, { method: 'POST' }),
  
  resume: (id: string) =>
    api<{ success: boolean; message: string; currentQuestion: Question | null }>(`/api/sessions/${id}/resume`, { method: 'POST' }),
  
  get: (id: string) => api<SessionDetail>(`/api/sessions/${id}`),
  
  list: () => api<SessionSummary[]>('/api/sessions'),
  
  generateBlueprint: (id: string) =>
    api<{ blueprintId: string; title: string; markdown: string }>(`/api/sessions/${id}/generate-blueprint`, { method: 'POST' }),
  
  getBlueprint: (id: string) =>
    api<Blueprint>(`/api/sessions/${id}/blueprint`),
};

// 知人API
export const contactsApi = {
  list: () => api<Contact[]>('/api/contacts'),
  
  create: (data: Omit<Contact, 'id' | 'createdAt'>) =>
    api<Contact>('/api/contacts', {
      method: 'POST',
      body: data,
    }),
  
  update: (id: string, data: Partial<Contact>) =>
    api<Contact>(`/api/contacts/${id}`, {
      method: 'PUT',
      body: data,
    }),
  
  delete: (id: string) =>
    api<{ success: boolean }>(`/api/contacts/${id}`, { method: 'DELETE' }),
};

// 設計書API
export const blueprintsApi = {
  list: () => api<BlueprintSummary[]>('/api/blueprints'),
  
  get: (id: string) => api<Blueprint>(`/api/blueprints/${id}`),
  
  updateStatus: (id: string, status: string) =>
    api<{ id: string; status: string }>(`/api/blueprints/${id}/status`, {
      method: 'PUT',
      body: { status },
    }),
};

// 音声API
export const voiceApi = {
  transcribe: async (audio: Blob) => {
    const formData = new FormData();
    formData.append('audio', audio);
    
    const response = await fetch(`${API_BASE_URL}/api/voice/transcribe`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new ApiError(response.status, error.error, error.message);
    }
    
    return response.json() as Promise<{ transcript: string; confidence: number }>;
  },
  
  createSession: (guestName?: string, voice?: string, initialIdea?: string) =>
    api<{ sessionId: string; token: string; expiresAt: number; endpoint: string; model: string }>('/api/voice/session', {
      method: 'POST',
      body: { guestName, voice, initialIdea },
    }),
  
  status: () =>
    api<{ available: boolean; transcriptionAvailable: boolean; provider: string; defaultVoice: string }>('/api/voice/status'),
  
  synthesize: (text: string, voice?: string) =>
    api<{ audioUrl: string; contentType: string }>('/api/voice/synthesize', {
      method: 'POST',
      body: { text, voice },
    }),
};

// 型定義
export interface User {
  id: string;
  email: string;
  name: string;
  role: 'admin' | 'user' | 'viewer';
  status?: 'active' | 'pending' | 'disabled';
  avatarUrl?: string | null;
  isMaster?: boolean;
  lastLoginAt?: string;
  createdAt?: string;
}

export interface AiModel {
  id: string;
  name: string;
  provider: string;
  description: string;
  isDefault: boolean;
}

export interface RealtimeSession {
  success: boolean;
  sessionId: string;
  clientSecret: string;
  expiresAt: number;
  websocketUrl: string;
  model: string;
  voice: string;
}

export interface StartSessionData {
  guestEmail?: string;
  guestName?: string;
  guestCompany?: string;
  guestPhone?: string;
  initialIdea?: string;
  aiModel?: string;
  aiModelId?: string;
}

export interface KycData {
  name: string;
  company?: string;
  email: string;
  phone?: string;
  relationship?: string;
}

export interface Question {
  id: string;
  number: number;
  category: string;
  text: string;
  questionText?: string;
}

export interface AnswerResponse {
  message: string;
  answeredQuestions: number;
  totalQuestions: number;
  isComplete: boolean;
  nextQuestion: Question | null;
}

export interface SessionDetail {
  id: string;
  userId: string | null;
  guestEmail: string | null;
  guestName: string | null;
  initialIdea: string | null;
  status: string;
  totalQuestions: number;
  answeredQuestions: number;
  questions: Array<{
    id: string;
    questionNumber: number;
    category: string;
    questionText: string;
    isAnswered: boolean;
  }>;
  conversationLogs: Array<{
    id: string;
    role: string;
    content: string;
    createdAt: string;
  }>;
}

export interface SessionSummary {
  id: string;
  initialIdea: string | null;
  status: string;
  totalQuestions: number;
  answeredQuestions: number;
  createdAt: string;
}

export interface Session {
  id: string;
  userId?: string | null;
  guestEmail?: string | null;
  guestName?: string | null;
  initialIdea?: string | null;
  status: string;
  totalQuestions: number;
  answeredQuestions: number;
  currentQuestion?: Question | null;
  createdAt: string;
}

export interface Contact {
  id: string;
  email?: string | null;
  name: string;
  company?: string | null;
  role?: string | null;
  affiliation?: string | null;
  position?: string | null;
  phone?: string | null;
  notes?: string | null;
  relationship?: string | null;
  isVerified?: boolean;
  createdAt?: string;
}

export interface Blueprint {
  id: string;
  sessionId: string;
  title: string;
  markdownContent: string;
  projectConcept?: object;
  businessRequirements?: object;
  systemScope?: object;
  uiVibe?: object;
  content?: Record<string, unknown>;
  status: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface BlueprintSummary {
  id: string;
  sessionId: string;
  title: string;
  status: string;
  version: number;
  createdAt: string;
}

